import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Hls from 'hls.js';
import { useVideoContext } from './Video';
import { CaptionSettings } from '@/types/project';
import {
  getEffectiveCaptionSettings,
  getCaptionFontFamily,
  getCaptionFontWeight,
} from '@/components/CaptionSettingsPanel';
import type { FontOption } from '@/components/CaptionSettingsPanel';
import captionStyles from './VideoPlayer.module.scss';

// Reference width for scaling (standard 1080p video width)
// Caption sizes are designed to look correct at this reference width
const REFERENCE_VIDEO_WIDTH = 1920;
const BASE_CAPTION_FONT_SIZE = 48; // Base font size at reference width
const BASE_CAPTION_PADDING_V = 12; // Vertical padding at reference width
const BASE_CAPTION_PADDING_H = 24; // Horizontal padding at reference width
const BASE_CAPTION_BOTTOM = 80; // Bottom offset at reference width
const BASE_HIGHLIGHT_SHADOW = 6; // Highlight box-shadow extension at reference width
const BASE_BORDER_RADIUS = 8; // Border radius at reference width

interface WordItem {
  start: number;
  end: number;
  text: string;
}

interface OverlayImageAttr {
  image?: Blob;
  position?: React.CSSProperties;
}

interface Props {
  start?: number; // seconds
  end?: number; // seconds
  headingTitle?: string;
  headingStyle?: React.CSSProperties;
  headingContainerStyle?: React.CSSProperties;
  logo?: string;
  imageAtr?: OverlayImageAttr;
  addLogo?: boolean;
  addCaptions?: boolean;
  captionSettings?: CaptionSettings;
  transcriptData?: {
    items: WordItem[];
  } | null;
  /**
   * Start time of the segment in the original video (seconds). Accepted for API
   * parity with VideoPlayer. NOTE: this HLS player plays the original video clamped
   * to [start, end], so `video.currentTime` is already in original-video coordinates
   * and matches transcript timings directly — no offset is applied here.
   */
  segmentStartTime?: number;
}

// A lightweight HLS-aware player that clamps playback between start and end.
// - Uses native HLS on Safari, hls.js elsewhere.
// - Prevents scrubbing/rewinding before `start` and past `end`.
const HlsClippedPlayer: FC<Props> = ({
  start = 0,
  end,
  headingTitle,
  headingStyle,
  headingContainerStyle,
  logo,
  imageAtr,
  addLogo,
  addCaptions,
  captionSettings,
  transcriptData,
}) => {
  const context = useVideoContext();
  const { videoUrl } = context;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [segDuration, setSegDuration] = useState<number>(Math.max(0, (end ?? 0) - (start || 0)));
  const [currentSegTime, setCurrentSegTime] = useState<number>(0);
  // Absolute video time (original-video coordinates) used for caption sync.
  const [captionTime, setCaptionTime] = useState<number>(0);
  const [captionScale, setCaptionScale] = useState<number>(1);
  const [adaptiveBorderColor, setAdaptiveBorderColor] = useState<string>('#000000');

  // Attach/detach media source
  useEffect((): void | (() => void) => {
    const video = videoRef.current;
    if (!video || !videoUrl) return undefined;

    const isHls =
      /\.m3u8($|\?)/i.test(videoUrl) ||
      videoUrl.includes('format=m3u8') ||
      videoUrl.includes('application/vnd.apple.mpegurl');

    const setupNative = (): void => {
      video.src = videoUrl;
    };

    if (isHls) {
      const canNative = video.canPlayType('application/vnd.apple.mpegurl');
      if (canNative) {
        setupNative();
      } else if (Hls.isSupported()) {
        const hls = new Hls({ enableWorker: true });
        hls.loadSource(videoUrl);
        hls.attachMedia(video);
        hlsRef.current = hls;
      } else {
        // Fallback: try setting src directly
        setupNative();
      }
    } else {
      setupNative();
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      video.removeAttribute('src');
      try {
        video.load();
      } catch (e) {
        // ignore
      }
    };
  }, [videoUrl]);

  // Enforce clamped playback range and initial position
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const clamp = (t: number): number => {
      const s = Math.max(0, start || 0);
      const e = end && end > 0 ? end : Number.POSITIVE_INFINITY;
      if (t < s) return s;
      if (t > e) return e;
      return t;
    };

    const handleLoadedMetadata = (): void => {
      // Seek to the start of the segment
      try {
        video.currentTime = clamp(start || 0);
      } catch (_) {
        // some browsers require a play() before precise seek; ignore
      }
      const computedEnd = end != null ? end : isFinite(video.duration) ? video.duration : undefined;
      if (computedEnd != null) {
        setSegDuration(Math.max(0, computedEnd - (start || 0)));
      }
      setCurrentSegTime(0);
    };

    const handleSeeking = (): void => {
      const desired = clamp(video.currentTime);
      if (Math.abs(desired - video.currentTime) > 0.01) {
        video.currentTime = desired;
      }
    };

    const handleTimeUpdate = (): void => {
      if (end != null && video.currentTime >= end) {
        // Halt playback exactly at end boundary
        video.currentTime = end;
        video.pause();
        setIsPlaying(false);
      }
      // Prevent drifting before start due to keyboard shortcuts or programmatic seeks
      if (video.currentTime < (start || 0)) {
        video.currentTime = start || 0;
      }
      const within = Math.max(0, video.currentTime - (start || 0));
      setCurrentSegTime(within);
      // Absolute time for caption lookup (matches original-video transcript timings).
      setCaptionTime(video.currentTime);
    };

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('seeking', handleSeeking);
    video.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('seeking', handleSeeking);
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [start, end]);

  // Derived image URL for overlay if Blob provided
  const overlayImgUrl = imageAtr?.image ? URL.createObjectURL(imageAtr.image) : undefined;

  // Sync isPlaying with element state (e.g., if user agent auto-pauses)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onPlay = (): void => setIsPlaying(true);
    const onPause = (): void => setIsPlaying(false);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    return (): void => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
    };
  }, []);

  const togglePlay = useCallback((): void => {
    const video = videoRef.current;
    if (!video) return;
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      const s = start || 0;
      const e = end ?? Number.POSITIVE_INFINITY;
      if (video.currentTime >= e - 0.01) {
        video.currentTime = s;
      }
      void video.play();
      setIsPlaying(true);
    }
  }, [isPlaying, start, end]);

  const onScrub = useCallback(
    (value: number): void => {
      const video = videoRef.current;
      if (!video) return;
      const s = start || 0;
      const duration = segDuration || 0;
      const v = Math.max(0, Math.min(duration, value));
      video.currentTime = s + v;
      setCurrentSegTime(v);
    },
    [start, segDuration],
  );

  const fmt = useCallback((t: number): string => {
    if (!isFinite(t)) return '0:00';
    const sign = t < 0 ? '-' : '';
    const abs = Math.max(0, Math.floor(Math.abs(t)));
    const m = Math.floor(abs / 60);
    const s = abs % 60;
    return `${sign}${m}:${s.toString().padStart(2, '0')}`;
  }, []);

  const durationLabel = useMemo(() => fmt(segDuration || 0), [fmt, segDuration]);
  const currentLabel = useMemo(() => fmt(currentSegTime || 0), [fmt, currentSegTime]);

  // ----- Caption rendering (ported from VideoPlayer) -----
  // Track caption container size and calculate caption scale factor.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId: number | null = null;

    const updateScale = (): void => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        const containerWidth = container.offsetWidth;
        const scale = containerWidth / REFERENCE_VIDEO_WIDTH;
        setCaptionScale(scale);
        rafId = null;
      });
    };

    updateScale();

    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(container);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, []);

  // Get effective caption settings
  const effectiveCaptionSettings = useMemo(() => {
    if (!captionSettings) return null;
    return getEffectiveCaptionSettings(captionSettings);
  }, [captionSettings]);

  // Analyze video brightness in the bottom 25% for adaptive contrast
  useEffect(() => {
    if (!effectiveCaptionSettings?.adaptiveContrast) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    let animationId: number | null = null;

    const analyzeFrame = (): void => {
      if (video.paused || video.ended) {
        animationId = requestAnimationFrame(analyzeFrame);
        return;
      }

      const sampleWidth = 160;
      const sampleHeight = 90;
      canvas.width = sampleWidth;
      canvas.height = sampleHeight;

      ctx.drawImage(video, 0, 0, sampleWidth, sampleHeight);

      const bottomStartY = Math.floor(sampleHeight * 0.75);
      const bottomHeight = sampleHeight - bottomStartY;

      let imageData;
      try {
        imageData = ctx.getImageData(0, bottomStartY, sampleWidth, bottomHeight);
      } catch (e) {
        // Canvas tainted by cross-origin data - skip analysis
        animationId = requestAnimationFrame(analyzeFrame);
        return;
      }
      const pixels = imageData.data;

      let totalBrightness = 0;
      const pixelCount = pixels.length / 4;

      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
        totalBrightness += brightness;
      }

      const avgBrightness = totalBrightness / pixelCount;
      const newBorderColor = avgBrightness < 128 ? '#FFFFFF' : '#000000';
      setAdaptiveBorderColor(newBorderColor);

      animationId = requestAnimationFrame(analyzeFrame);
    };

    const handlePlay = (): void => {
      analyzeFrame();
    };

    const handlePauseAnalyze = (): void => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePauseAnalyze);
    video.addEventListener('ended', handlePauseAnalyze);

    if (!video.paused) {
      analyzeFrame();
    }

    return () => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
      }
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePauseAnalyze);
      video.removeEventListener('ended', handlePauseAnalyze);
    };
  }, [effectiveCaptionSettings?.adaptiveContrast]);

  // Group words into caption segments
  const captionSegments = useMemo(() => {
    if (!transcriptData?.items) return [];
    const segments: { start: number; end: number; words: WordItem[] }[] = [];
    let currentSegment: { start: number; end: number; words: WordItem[] } | null = null;

    transcriptData.items.forEach((word) => {
      if (!currentSegment) {
        currentSegment = { start: word.start, end: word.end, words: [word] };
      } else {
        const timeSinceStart = word.end - currentSegment.start;
        const gapFromPrevious = word.start - currentSegment.end;

        if (timeSinceStart > 3 || gapFromPrevious > 1) {
          segments.push(currentSegment);
          currentSegment = { start: word.start, end: word.end, words: [word] };
        } else {
          currentSegment.end = word.end;
          currentSegment.words.push(word);
        }
      }
    });

    if (currentSegment) {
      segments.push(currentSegment);
    }

    return segments;
  }, [transcriptData]);

  // Current caption segment. captionTime is already absolute (original-video time),
  // so no segmentStartTime offset is applied here.
  const getCurrentSegment = useCallback(
    (time: number) =>
      captionSegments.find((seg) => time >= seg.start && time <= seg.end + 0.5) || null,
    [captionSegments],
  );

  // Current highlighted word (absolute time).
  const getCurrentHighlightedWord = useCallback(
    (time: number): WordItem | null => {
      if (!transcriptData?.items) return null;
      return transcriptData.items.find((item) => time >= item.start && time <= item.end) || null;
    },
    [transcriptData],
  );

  // Render caption text with word-level highlighting
  const renderCaptionText = useCallback(() => {
    if (!effectiveCaptionSettings) return null;

    const segment = getCurrentSegment(captionTime);
    if (!segment) return null;

    const highlightedWord = effectiveCaptionSettings.highlightEnabled
      ? getCurrentHighlightedWord(captionTime)
      : null;

    return (
      <span>
        {segment.words.map((word, index) => {
          const isHighlighted = highlightedWord && word.start === highlightedWord.start;

          const wordStyle: React.CSSProperties = isHighlighted
            ? {
                backgroundColor: effectiveCaptionSettings.highlightColor,
                color: effectiveCaptionSettings.highlightWordColor,
              }
            : {};

          const scaledShadowSize = Math.round(BASE_HIGHLIGHT_SHADOW * captionScale);
          const scaledWordStyle: React.CSSProperties = isHighlighted
            ? {
                ...wordStyle,
                borderRadius: `${Math.round(3 * captionScale)}px`,
                boxShadow: `${scaledShadowSize}px 0 0 ${effectiveCaptionSettings.highlightColor}, -${scaledShadowSize}px 0 0 ${effectiveCaptionSettings.highlightColor}`,
              }
            : wordStyle;

          return (
            <span key={`${word.start}-${index}`}>
              <span style={scaledWordStyle}>{word.text}</span>
              {index < segment.words.length - 1 ? ' ' : ''}
            </span>
          );
        })}
      </span>
    );
  }, [
    captionTime,
    effectiveCaptionSettings,
    getCurrentSegment,
    getCurrentHighlightedWord,
    captionScale,
  ]);

  // Get scaled text shadow for border effect
  const getScaledTextShadow = useCallback(() => {
    if (effectiveCaptionSettings?.adaptiveContrast) {
      const baseWidth = 8 / 2;
      const scaledWidth = Math.max(1, Math.round(baseWidth * captionScale));
      const color = adaptiveBorderColor;

      const shadows = [];
      for (let x = -scaledWidth; x <= scaledWidth; x++) {
        for (let y = -scaledWidth; y <= scaledWidth; y++) {
          if (x !== 0 || y !== 0) {
            shadows.push(`${x}px ${y}px 0 ${color}`);
          }
        }
      }
      return shadows.join(', ');
    }

    if (!effectiveCaptionSettings?.borderEnabled) return 'none';

    const baseWidth = parseInt(effectiveCaptionSettings.borderWidth, 10) / 2;
    const scaledWidth = Math.max(1, Math.round(baseWidth * captionScale));
    const color = effectiveCaptionSettings.borderColor;

    const shadows = [];
    for (let x = -scaledWidth; x <= scaledWidth; x++) {
      for (let y = -scaledWidth; y <= scaledWidth; y++) {
        if (x !== 0 || y !== 0) {
          shadows.push(`${x}px ${y}px 0 ${color}`);
        }
      }
    }
    return shadows.join(', ');
  }, [effectiveCaptionSettings, captionScale, adaptiveBorderColor]);

  // Caption container style with scaled dimensions
  const captionTextStyle: React.CSSProperties = useMemo(() => {
    if (!effectiveCaptionSettings) return {};
    return {
      fontFamily: getCaptionFontFamily(effectiveCaptionSettings.captionFont as FontOption),
      fontWeight: getCaptionFontWeight(effectiveCaptionSettings.captionFont as FontOption),
      color: effectiveCaptionSettings.captionColor,
      textShadow: getScaledTextShadow(),
      fontSize: `${Math.round(BASE_CAPTION_FONT_SIZE * captionScale)}px`,
      padding: `${Math.round(BASE_CAPTION_PADDING_V * captionScale)}px ${Math.round(
        BASE_CAPTION_PADDING_H * captionScale,
      )}px`,
      borderRadius: `${Math.round(BASE_BORDER_RADIUS * captionScale)}px`,
      lineHeight: 1.4,
    };
  }, [effectiveCaptionSettings, getScaledTextShadow, captionScale]);

  // Caption overlay style with scaled bottom offset
  const captionOverlayStyle: React.CSSProperties = useMemo(
    () => ({
      bottom: `${Math.round(BASE_CAPTION_BOTTOM * captionScale)}px`,
      padding: `0 ${Math.round(20 * captionScale)}px`,
    }),
    [captionScale],
  );

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', justifyItems: 'center' }}>
      {headingTitle ? (
        <div style={{ width: '100%', ...headingContainerStyle }}>
          <div style={headingStyle}>{headingTitle}</div>
        </div>
      ) : null}

      <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
        <video
          ref={videoRef}
          controls={false}
          playsInline
          // NOTE: no crossOrigin — the HLS/S3 source may lack CORS headers on
          // deployed envs, and crossOrigin would block playback. Adaptive-contrast
          // frame analysis is guarded by try/catch and skips on canvas taint.
          // Preload metadata to be able to seek to `start` quickly
          preload="metadata"
          style={{ maxHeight: '50vh', display: 'block' }}
        />

        {addLogo && (overlayImgUrl || logo) ? (
          <img
            src={logo ?? overlayImgUrl}
            alt="overlay"
            style={{ imageRendering: 'pixelated', ...(imageAtr?.position || {}) }}
          />
        ) : null}

        {/* Hidden canvas for video frame analysis (adaptive contrast) */}
        <canvas ref={canvasRef} style={{ display: 'none' }} aria-hidden="true" />

        {/* Caption Overlay - sizes scale with video container */}
        {addCaptions && transcriptData && (
          <div className={captionStyles.captionOverlay} style={captionOverlayStyle}>
            <div className={captionStyles.captionText} style={captionTextStyle}>
              {renderCaptionText()}
            </div>
          </div>
        )}
      </div>

      {/* Minimal custom controls showing only segment duration */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, width: '100%' }}>
        <button type="button" onClick={togglePlay} style={{ padding: '4px 8px' }}>
          {isPlaying ? 'Pause' : 'Play'}
        </button>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{currentLabel}</span>
        <input
          type="range"
          min={0}
          max={Math.max(0.01, segDuration || 0)}
          step={0.1}
          value={Math.min(currentSegTime, segDuration || 0)}
          onChange={(e) => onScrub(parseFloat(e.target.value))}
          style={{ flex: 1 }}
        />
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{durationLabel}</span>
      </div>
    </div>
  );
};

export default HlsClippedPlayer;
